import Tooltip from '@mui/material/Tooltip';
import React, { createRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { wordApi } from 'api';
import { useModal } from 'shared/hooks';
import { ItemActionModal } from 'shared/ui/ItemActionModal';

import wordRedSvg from '../../../assets/img/file-doc-red.svg';
import wordSvg from '../../../assets/img/file-doc.svg';

interface Props {
  currentICLId: string;
  id: string;
  studentWordUrl?: string;
  hasText?: boolean;
  closeModal?: () => void;
}

export const StudentWordInput = ({ currentICLId, id, studentWordUrl, hasText, closeModal }: Props) => {
  const { t } = useTranslation();
  const inputRef = createRef<HTMLInputElement>();
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, close, open } = useModal();

  const sendWord = async () => {
    const wordInput = inputRef.current;

    if (!wordInput || !wordInput.files) {
      return;
    }

    setIsLoading(true);

    const file = wordInput.files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      await wordApi.addWord(file, currentICLId, id, true);
      toast.success(t('Successfully added!'));
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsLoading(false);
  };

  const removeWord = async () => {
    if (!studentWordUrl) {
      return;
    }

    setIsLoading(true);

    try {
      await wordApi.removeWord(studentWordUrl, currentICLId, id, true);
      toast.success(t('Successfully removed!'));
      close();
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsLoading(false);
  };

  return (
    <div className="ql-image button-icon">
      <Tooltip
        title={studentWordUrl ? t('remove word') : t('add word')}
        componentsProps={{
          popper: {
            sx: {
              ['& .MuiTooltip-arrow']: {
                color: '#9CDFFC',
              },
              ['& .MuiTooltip-tooltip']: {
                backgroundColor: '#9CDFFC',
                color: '#000000',
                fontSize: '14px',
                padding: '14px',
                borderRadius: '8px',
              },
            },
          },
        }}
        arrow
      >
        <button
          onClick={() => {
            closeModal && closeModal();

            if (studentWordUrl) {
              open();
            } else {
              inputRef.current?.click();
            }
          }}
          className={
            hasText
              ? 'flex items-center gap-[10px]'
              : 'btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600'
          }
        >
          {studentWordUrl ? <img src={wordRedSvg} alt="camera" /> : <img src={wordSvg} alt="camera" />}

          <input
            id="wordInput"
            type="file"
            className="hidden"
            accept="application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            ref={inputRef}
            onChange={sendWord}
            disabled={isLoading || !!studentWordUrl}
            title={t('add word')}
          />

          {hasText && (
            <p className="!mb-0 text-base font-bold text-[#242C2C]">
              {studentWordUrl ? t('remove word') : t('add word')}
            </p>
          )}
        </button>
      </Tooltip>

      <ItemActionModal
        isOpen={isOpen}
        close={close}
        title={t('remove word')}
        onHandle={removeWord}
        isSubmitButtonDisabled={isLoading}
        buttonTitle="delete"
      />
    </div>
  );
};
